import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient, useTopics } from '../../api';
import type { LifeEventDto, TopLevelTopicDto } from '../../api/generated/client';
import type { EventWithDefaultDate } from '../../interfaces/event';
import { hasEntry } from '../../utils/utils';
import { NeedEventModal } from '../topic/modals/NeedEventModal';
import { LifePlanModalStep, topicEventMap } from './constants';
import { SelectEventModal } from './modals/SelectEventModal';
import { SelectTopicModal } from './modals/SelectTopicModal';

interface IProps {
    onClose: () => void;
}

export const AddEventFlowModal: FunctionComponent<IProps> = ({ onClose }) => {
    const { i18n } = useTranslation();
    const { data: activeClient } = useClient();
    const { data: topics } = useTopics();

    const [modalStep, setModalStep] = useState(LifePlanModalStep.SELECT_TOPIC);

    const [activeTopic, setActiveTopic] = useState<TopLevelTopicDto | undefined>(undefined);
    const [activeEvent, setActiveEvent] = useState<EventWithDefaultDate>();

    if (!hasEntry(topics)) {
        return null;
    }

    const onTopicSelected = (topic?: TopLevelTopicDto) => {
        if (topic) {
            setActiveTopic({ ...topic });
        } else {
            setActiveTopic(topic);
        }
        setModalStep(LifePlanModalStep.SELECT_EVENT);
    };

    const onEventSelected = (selectedEvent: EventWithDefaultDate) => {
        setActiveEvent(selectedEvent);
        setModalStep(LifePlanModalStep.DEFINE_EVENT_DETAILS);
    };
    const onBack = () => {
        setActiveEvent(undefined);
        setModalStep(LifePlanModalStep.SELECT_EVENT);
    };

    const getNewEvent = (): LifeEventDto => ({
        id: undefined,
        dueDate: '',
        reminderDate: undefined,
        clientId: activeClient!.id,
        topicId: activeTopic!.id,
        topicKey: undefined,
        topicName: undefined,
        createdAt: undefined,
        note: undefined,
        name: activeEvent!.translation[i18n.language],
        isCashFlowRelevant: false,
        eventType: 'LIFE_EVENT',
        status: 'DEFINED',
        notification: 'NONE',
        type: undefined,
        currency: activeClient!.currency,
        amount: undefined,
        enforceDisable: undefined,
        signedAmount: undefined,
    });

    return (
        <>
            <SelectTopicModal isOpen={modalStep === LifePlanModalStep.SELECT_TOPIC} onTopicSelected={onTopicSelected} isAddEvent={true} onClose={onClose} />
            <SelectEventModal
                show={modalStep === LifePlanModalStep.SELECT_EVENT}
                topics={topics}
                selectedTopic={activeTopic}
                topicEventMap={topicEventMap}
                onClose={onClose}
                onTopicSelected={onTopicSelected}
                onEventSelected={onEventSelected}
            />
            {modalStep === LifePlanModalStep.DEFINE_EVENT_DETAILS && (
                <NeedEventModal isOpen={modalStep === LifePlanModalStep.DEFINE_EVENT_DETAILS} onClose={onClose} event={getNewEvent()} onBack={onBack} />
            )}
        </>
    );
};
